import { REPORT_GUIDES_AVAILABLE } from './guideAvailability';
import React from 'react';
import { Link, useInRouterContext } from 'react-router-dom';
import { BookOpen, X } from 'lucide-react';
import type { ReportGuideStepId } from './reportGuideSteps';
import type { ReportGuideProgress } from './reportGuideStore';
import { dismissGuideTip, isGuideTipDismissed } from './reportGuideStore';
import {
  shouldShowExplainerHints,
  EXPLAINER_HINTS_CHANGED_EVENT,
} from '../../lib/explainerHintsPref';

interface Props {
  tipId: string;
  stepId: ReportGuideStepId;
  title: string;
  body: string;
  uid: string | null;
  progress: ReportGuideProgress;
  onProgressChange: (next: ReportGuideProgress) => void;
}

export const ContextualGuideTip: React.FC<Props> = ({
  tipId,
  stepId,
  title,
  body,
  uid,
  progress,
  onProgressChange,
}) => {
  const dismissed = isGuideTipDismissed(progress, tipId);
  const inRouter = useInRouterContext();
  const guideHref = `/guide/reports?step=${stepId}`;

  // Global show/hide for the inline explainer/help cards, controlled by the
  // "Show help hints" toggle in the UserMenu (default ON for the first 5
  // logins, then OFF). Read on mount, then re-read whenever the toggle fires
  // the change event so visible cards update live without a reload.
  const [hintsOn, setHintsOn] = React.useState(shouldShowExplainerHints);
  React.useEffect(() => {
    const sync = () => setHintsOn(shouldShowExplainerHints());
    // Same-tab toggle dispatches the custom event; cross-tab uses `storage`.
    window.addEventListener(EXPLAINER_HINTS_CHANGED_EVENT, sync);
    window.addEventListener('storage', sync);
    // Re-sync on mount in case the pref changed before this card mounted.
    sync();
    return () => {
      window.removeEventListener(EXPLAINER_HINTS_CHANGED_EVENT, sync);
      window.removeEventListener('storage', sync);
    };
  }, []);

  if (!REPORT_GUIDES_AVAILABLE || !hintsOn) return null;
  if (dismissed) return null;

  return (
    <div
      className="flex items-start gap-2 p-2 border border-primary/30 rounded-lg bg-primary/5 text-xs"
      data-testid={`contextual-guide-tip-${tipId}`}
      role="note"
    >
      <BookOpen className="w-3.5 h-3.5 text-primary shrink-0 mt-0.5" aria-hidden />
      <div className="flex-1 min-w-0 space-y-1">
        <div className="font-bold uppercase tracking-widest text-text-primary">{title}</div>
        <p className="text-text-secondary leading-relaxed">{body}</p>
        {inRouter ? (
          <Link
            to={guideHref}
            className="inline-block type-meta uppercase tracking-widest text-primary hover:underline"
            data-testid={`contextual-guide-open-${tipId}`}
          >
            Open full guide
          </Link>
        ) : (
          <a
            href={guideHref}
            className="inline-block type-meta uppercase tracking-widest text-primary hover:underline"
            data-testid={`contextual-guide-open-${tipId}`}
          >
            Open full guide
          </a>
        )}
      </div>
      <button
        type="button"
        className="p-0.5 text-text-muted hover:text-text-primary shrink-0"
        aria-label="Dismiss tip"
        data-testid={`contextual-guide-dismiss-${tipId}`}
        onClick={() => onProgressChange(dismissGuideTip(uid, progress, tipId))}
      >
        <X className="w-3.5 h-3.5" />
      </button>
    </div>
  );
};
